import { Alert, AlertTitle, Box, FormControlLabel, Stack, Switch, Typography } from "@mui/material";
import { useCallback, useEffect, useRef, useState } from "react";

import type { EmailSetting } from "../../api/emailSettingsApi";
import { useAuthedApi } from "../../api/useAuthedApi";
import { PageHeader } from "../../components/Common/PageHeader";
import { WorkspaceCard } from "../../components/Common/WorkspaceCard";

function message(e: unknown): string {
  return e instanceof Error && e.message ? e.message : "Something went wrong";
}

/** One line saying where the in-effect value came from.
 *
 *  Uses the server-computed `source` rather than comparing values: a company
 *  that deliberately chose the same value as the platform default IS
 *  overridden, and must not silently start following the default when it
 *  changes. */
function sourceLabel(source: EmailSetting["source"]): string {
  if (source === "company") return "Set for this organization.";
  if (source === "platform") return "Inherited from the platform default.";
  return "Not set anywhere — off.";
}

/** Whether Artie may send email on a member's behalf, for this organization.
 *
 *  OFF BY DEFAULT, and deliberately harder to turn on than the other AI
 *  settings: everything else here changes what the agent costs or says, and
 *  this one puts mail in someone else's inbox under this organization's name.
 *  That is the only setting on this page with a consequence outside the
 *  product, so it states what it permits before it is flipped rather than
 *  after.
 *
 *  ADMIN ONLY, and not by hiding the panel. `editable` comes from the server,
 *  which resolves the role from FGA rather than the token; a member sees the
 *  organization's current position and why they cannot change it, instead of a
 *  live-looking switch that 403s. Hiding it would also hide the ANSWER to "can
 *  this org send mail", which a member has a legitimate reason to want.
 */
export function AgentEmailSettings() {
  const api = useAuthedApi();
  const [setting, setSetting] = useState<EmailSetting | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      setSetting(await api.emailSettings.load());
    } catch (e) {
      setError(message(e));
    }
  }, [api]);

  // `loadedRef` rather than a plain dependency list: useAuthedApi() returns a
  // fresh object every render, so an unguarded effect refires on each one and
  // wipes the error or notice the user is reading.
  const loadedRef = useRef(false);
  useEffect(() => {
    if (loadedRef.current) return;
    loadedRef.current = true;
    void load();
  }, [load]);

  const toggle = async (enabled: boolean) => {
    // Guarded here as well as on the control. `disabled` is a rendering hint
    // and nothing more: a programmatic click still reaches this handler (jsdom
    // dispatches one), and the panel should not fire a write it has already
    // been told will 403. The API re-checks regardless — this only keeps the UI
    // from claiming an attempt it had no business making.
    if (setting && setting.editable !== true) return;
    setSaving(true);
    setError(null);
    setNotice(null);
    try {
      // The RESPONSE becomes the state, not the submitted value: the server
      // recomputes `source`, which flips the moment a field is cleared.
      setSetting(await api.emailSettings.save({ enabled }));
      setNotice(
        enabled
          ? "Artie can now propose emails. Every one still needs approval."
          : "Artie can no longer send email for this organization.",
      );
    } catch (e) {
      setError(message(e));
    } finally {
      setSaving(false);
    }
  };

  const editable = setting?.editable === true;

  return (
    <Box>
      <PageHeader
        title="Agent email"
        subtitle="Let Artie draft and send email on a member's behalf. Off unless you turn it on."
      />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {notice && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setNotice(null)}>
          {notice}
        </Alert>
      )}

      <WorkspaceCard>
        <Stack spacing={3}>
          {/* Before the switch, not after it: this is what turning it on
              permits, and it is the only setting on this page whose blast
              radius is outside the product. */}
          <Alert severity="warning">
            <AlertTitle>This sends real email to real people</AlertTitle>
            <Typography variant="body2" component="div">
              With this on, Artie can compose and send email from this
              organization, with the requesting member's address as Reply-To.
              <Box component="ul" sx={{ pl: 2.5, mb: 0, mt: 1 }}>
                <li>
                  Every send stops for a human first. The approval card shows the
                  exact recipients, subject, body and attachments, and only
                  approve or reject are honoured — the text cannot be edited
                  after it is shown.
                </li>
                <li>
                  Recipients are checked against your member directory, so a
                  colleague, a likely typo and an outside address are told apart
                  before you approve.
                </li>
                <li>Every attempt is recorded, whether or not it was sent.</li>
              </Box>
            </Typography>
          </Alert>

          <Box>
            <FormControlLabel
              control={
                <Switch
                  checked={!!setting?.enabled}
                  disabled={!setting || saving || !editable}
                  onChange={(e) => void toggle(e.target.checked)}
                />
              }
              label="Allow Artie to send email"
            />
            <Typography variant="body2" color="text.secondary">
              {setting ? sourceLabel(setting.source) : "Loading…"}
            </Typography>
            {setting && !editable && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Only an organization admin can change this.
              </Typography>
            )}
          </Box>
        </Stack>
      </WorkspaceCard>
    </Box>
  );
}
